import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeft, Save, Search, Filter, Trash2, Package,
  CheckSquare, AlertCircle, RefreshCw, ChevronDown, ChevronUp,
  Tag, X, Check, Clock, Users, FileEdit, Building2,
  Phone, Hash, ShoppingCart, ArrowUp, TrendingUp, Settings,
  ChevronLeft, ChevronRight, Loader, Euro, Barcode, Plus, Download, Upload
} from 'lucide-react';
import { FAMILIAS_PREDEFINIDAS } from '../constants/familias';
const API_URL = process.env.REACT_APP_API_URL;

// Componente del Panel Lateral
const SidePanel = ({ 
  isOpen, 
  setIsOpen,
  searchTerm,
  setSearchTerm,
  selectedProvider,
  setSelectedProvider,
  providers,
  handleClearFilters,
  selectedProducts,
  selectedFamily,
  setSelectedFamily,
  handleApplyFamily,
  handleDeleteMultiple,
  pendingChanges,
  handleSaveChanges,
  saving,
  productFilters,
  setProductFilters,
  familiasDisponibles
}) => {
  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed left-4 top-1/2 transform -translate-y-1/2 z-50 bg-white border border-gray-300 rounded-r-lg p-3 shadow-lg hover:bg-gray-50 transition-all duration-300"
        style={{
          left: isOpen ? '384px' : '4px'
        }}
      >
        {isOpen ? <ChevronLeft size={20} className="text-gray-600" /> : <ChevronRight size={20} className="text-gray-600" />}
      </button>

      {/* Side Panel */}
      <div 
        className="fixed left-0 top-16 bottom-0 bg-white border-r border-gray-200 shadow-lg transition-transform duration-300 z-40 overflow-hidden"
        style={{
          width: '380px',
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)'
        }}
      >
        <div className="h-full flex flex-col">
          
          {/* Panel Header */}
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-teal-50 to-green-50">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                <Settings size={20} className="mr-2 text-teal-600" />
                Panel de Control
              </h2>
            </div>
            <p className="text-sm text-gray-600">
              Filtros, acciones masivas y configuración de productos
            </p>
          </div>

          <div className="flex-1 overflow-y-auto">
            
            {/* SECCIÓN 1: Filtros */}
            <div className="border-b-4 border-teal-300 bg-gradient-to-r from-teal-50 to-emerald-50">
              <div className="p-4">
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Buscar productos
                    </label>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Ej: AGUJA 40, JERINGA 10ML..."
                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-lg shadow-sm transition-colors bg-white"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Búsqueda estricta por nombre de producto (todas las palabras deben coincidir)
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Proveedor
                    </label>
                    <div className="relative">
                      <select
                        value={selectedProvider}
                        onChange={(e) => setSelectedProvider(e.target.value)}
                        className="block w-full pl-3 pr-8 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-lg shadow-sm transition-colors appearance-none bg-white"
                      >
                        <option value="">Todos los proveedores</option>
                        {providers.map(provider => (
                          <option key={provider.id} value={provider.id}>
                            {provider.nombre}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                        <ChevronDown size={16} className="text-gray-500" />
                      </div>
                    </div>
                  </div>

                  {/* NUEVOS FILTROS ADICIONALES */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Precio min (€)</label>
                      <input
                        type="number"
                        value={productFilters.precio_min}
                        onChange={(e) => setProductFilters(prev => ({...prev, precio_min: e.target.value}))}
                        className="w-full px-2 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Precio max (€)</label>
                      <input
                        type="number"
                        value={productFilters.precio_max}
                        onChange={(e) => setProductFilters(prev => ({...prev, precio_max: e.target.value}))}
                        className="w-full px-2 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="999.99"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Marca
                    </label>
                    <input
                      type="text"
                      value={productFilters.marca}
                      onChange={(e) => setProductFilters(prev => ({...prev, marca: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-lg shadow-sm transition-colors bg-white"
                      placeholder="Filtrar por marca..."
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Familia
                    </label>
                    <input
                      type="text"
                      value={productFilters.familia}
                      onChange={(e) => setProductFilters(prev => ({...prev, familia: e.target.value}))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-lg shadow-sm transition-colors bg-white"
                      placeholder="Filtrar por familia..."
                    />
                  </div>
                  
                  <button 
                    onClick={handleClearFilters}
                    className="w-full px-3 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-all duration-200 shadow-sm font-medium text-sm flex items-center justify-center"
                  >
                    <RefreshCw size={16} className="mr-2" />
                    Limpiar Filtros
                  </button>
                </div>
              </div>
            </div>

            {/* SEPARADOR VISUAL */}
            <div className="h-4 bg-gradient-to-r from-gray-100 to-gray-200"></div>

            {/* SECCIÓN 2: Acciones Masivas */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50">
              <div className="p-4">
                
                <div className="space-y-4">
                  
                  {/* Asignar Familia */}
                  <div className="bg-white rounded-lg p-3 border border-orange-200 shadow-sm">
                    <h4 className="font-medium text-orange-800 mb-3 text-xs flex items-center">
                      <Tag size={14} className="mr-1" />
                      Asignar Familia
                    </h4>
                    
                    <div className="space-y-3">
                      <div className="relative">
                        <select
                          value={selectedFamily}
                          onChange={(e) => setSelectedFamily(e.target.value)}
                          className="block w-full px-2 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-xs appearance-none bg-white"
                        >
                          <option value="">Seleccionar familia...</option>
                          <option value="Sin familia">Sin familia</option>
                          {familiasDisponibles.map(familia => (
                            <option key={familia} value={familia}>
                              {familia}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                          <ChevronDown size={12} className="text-gray-500" />
                        </div>
                      </div>
                      
                      <button
                        onClick={handleApplyFamily}
                        disabled={selectedProducts.size === 0 || !selectedFamily}
                        className="w-full px-3 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-all duration-200 shadow-sm font-medium text-xs disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        <Tag size={12} className="mr-2" />
                        Aplicar a {selectedProducts.size} productos
                      </button>
                    </div>
                  </div>
                  
                  {/* Eliminar Productos */}
                  <div className="bg-white rounded-lg p-3 border border-red-200 shadow-sm">
                    <h4 className="font-medium text-red-800 mb-3 text-xs flex items-center">
                      <Trash2 size={14} className="mr-1" />
                      Eliminar Productos
                    </h4>
                    
                    <button
                      onClick={handleDeleteMultiple}
                      disabled={selectedProducts.size === 0}
                      className="w-full px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 shadow-sm font-medium text-xs disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      <Trash2 size={12} className="mr-2" />
                      Eliminar {selectedProducts.size} seleccionados
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* SEPARADOR VISUAL */}
            <div className="h-4 bg-gradient-to-r from-gray-100 to-gray-200"></div>

            {/* SECCIÓN 3: Guardar Cambios */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="p-4">
                <div className="bg-green-100 rounded-lg p-3 mb-4 border-l-4 border-green-500">
                  <h3 className="text-sm font-bold text-green-800 mb-1 flex items-center">
                    <Save size={18} className="mr-2" />
                    Cambios Pendientes
                  </h3>
                  <p className="text-xs text-green-700">
                    {pendingChanges > 0 ? `${pendingChanges} productos modificados` : 'No hay cambios pendientes'}
                  </p>
                </div>
                
                <button
                  onClick={handleSaveChanges}
                  disabled={pendingChanges === 0 || saving}
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 shadow-lg font-bold flex items-center justify-center text-sm border-2 border-green-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      <span>Guardando cambios...</span>
                    </>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      <span>GUARDAR CAMBIOS ({pendingChanges})</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ─── Modal: Gestión de códigos de barras de un producto ────────────────────────
const BarcodesProductModal = ({ open, product, onClose }) => {
  const [barcodes, setBarcodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ codigo: '', tipo: 'unidad', factor_unidades: 1 });

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const cargar = async () => {
    if (!product) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/productos/${product.id}/barcodes`, { headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Error');
      setBarcodes(d.barcodes || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setForm({ codigo: '', tipo: 'unidad', factor_unidades: 1 });
      cargar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product?.id]);

  if (!open || !product) return null;

  const handleCrear = async () => {
    const codigo = String(form.codigo || '').trim();
    if (!codigo) { setError('El código no puede estar vacío'); return; }
    const factor = form.tipo === 'caja' ? (parseInt(form.factor_unidades) || 0) : 1;
    if (form.tipo === 'caja' && factor < 1) { setError('Factor de caja debe ser >= 1'); return; }
    setSaving(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/productos/${product.id}/barcodes`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ codigo, tipo: form.tipo, factor_unidades: factor }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Error al crear');
      setForm({ codigo: '', tipo: 'unidad', factor_unidades: 1 });
      await cargar();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar este código de barras?')) return;
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/barcodes/${id}`, { method: 'DELETE', headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Error al eliminar');
      await cargar();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-teal-50">
          <div className="flex items-center gap-3">
            <Barcode size={22} className="text-teal-600" />
            <div>
              <h2 className="text-lg font-bold text-gray-800">Códigos de barras</h2>
              <p className="text-xs text-gray-600 max-w-md truncate">{product.nombre}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-lg">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Lista de barcodes existentes */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Asignados</h3>
            {loading ? (
              <div className="text-center text-gray-400 py-6"><Loader size={20} className="animate-spin mx-auto" /></div>
            ) : barcodes.length === 0 ? (
              <div className="text-center text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg p-6">
                Sin códigos de barras asignados.
              </div>
            ) : (
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2">Tipo</th>
                    <th className="text-left px-3 py-2">Código</th>
                    <th className="text-right px-3 py-2">Factor unidades</th>
                    <th className="px-3 py-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {barcodes.map(b => (
                    <tr key={b.id} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          b.tipo === 'caja' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                        }`}>{b.tipo}</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-gray-800">{b.codigo}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{b.factor_unidades}</td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => handleEliminar(b.id)}
                          className="text-red-600 hover:bg-red-50 p-1 rounded"
                          title="Eliminar"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Form de alta */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Plus size={16} className="text-teal-600" /> Añadir código
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
                <select
                  value={form.tipo}
                  onChange={(e) => setForm(f => ({ ...f, tipo: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded focus:outline-none focus:border-teal-500"
                >
                  <option value="unidad">Unidad</option>
                  <option value="caja">Caja</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Código</label>
                <input
                  type="text"
                  value={form.codigo}
                  onChange={(e) => setForm(f => ({ ...f, codigo: e.target.value }))}
                  placeholder="EAN/Código numérico"
                  className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded focus:outline-none focus:border-teal-500 font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Factor {form.tipo === 'caja' && <span className="text-amber-600">(unid/caja)</span>}
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.tipo === 'caja' ? form.factor_unidades : 1}
                  onChange={(e) => setForm(f => ({ ...f, factor_unidades: e.target.value }))}
                  disabled={form.tipo === 'unidad'}
                  className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded focus:outline-none focus:border-teal-500 disabled:bg-gray-100"
                />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={handleCrear}
                disabled={saving || !form.codigo.trim()}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded shadow flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
                Guardar
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              <strong>Unidad:</strong> escanear suma 1 unidad al stock.{' '}
              <strong>Caja:</strong> escanear suma N unidades (el factor).
            </p>
          </div>
        </div>

        <div className="px-6 py-3 border-t bg-gray-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-100">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default function UpdateProducts() {
  // Estado del cliente activo
  const [activeClient, setActiveClient] = useState(null);
  
  // Estados principales
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [providers, setProviders] = useState([]);
  
  // Estados de filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  
  // NUEVOS ESTADOS PARA FILTROS ADICIONALES
  const [productFilters, setProductFilters] = useState({
    precio_min: '',
    precio_max: '',
    marca: '',
    familia: ''
  });

  // Familias de los desplegables. Se construyen de TRES fuentes unidas, de
  // menos a mas fiable:
  //
  //   1. FAMILIAS_PREDEFINIDAS  — las 28 de siempre, como suelo minimo.
  //   2. Las familias que YA TRAEN los productos cargados. Esta es la clave:
  //      el listado viene con su campo `familia`, asi que si un producto esta
  //      en INFORMATICA la opcion aparece sin preguntar a nadie. No depende de
  //      ningun despliegue del backend.
  //   3. /api/productos/familias — las 167 de la base, cuando el backend este
  //      actualizado. Si falla o devuelve la lista antigua, 1 y 2 sostienen.
  //
  // Sin el punto 2, un producto en una familia fuera de la lista se mostraba
  // como "Sin familia": un <select> cae a su primera <option> cuando su value
  // no existe entre ellas. Se veia mal estando bien en la base, lo que invita a
  // "corregirlo" y machacar el dato.
  const [familiasApi, setFamiliasApi] = useState([]);

  const familiasDisponibles = useMemo(() => {
    const set = new Set(FAMILIAS_PREDEFINIDAS);
    familiasApi.forEach(f => set.add(f));
    products.forEach(p => {
      const f = (p.familia || '').trim();
      if (f && f !== 'Sin familia') set.add(f);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [familiasApi, products]);

  useEffect(() => {
    const cargarFamilias = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/productos/familias`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (Array.isArray(data.familias) && data.familias.length > 0) {
          setFamiliasApi(data.familias);
        }
      } catch (err) {
        // No es fatal: las fuentes 1 y 2 ya dan un desplegable utilizable.
        console.warn('No se pudieron cargar las familias del servidor:', err.message);
      }
    };
    cargarFamilias();
  }, []);

  // Estados de selección
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Estados de edición
  const [editedProducts, setEditedProducts] = useState(new Map());
  const [pendingChanges, setPendingChanges] = useState(0);
  
  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [selectedFamily, setSelectedFamily] = useState('');
  
  // Estados de confirmación
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: '', data: null });

  // Modal de gestión de códigos de barras de un producto
  const [barcodesModal, setBarcodesModal] = useState({ open: false, product: null });

  // Estado para el panel lateral
  const [sidePanelOpen, setSidePanelOpen] = useState(true);

  // **NUEVA FUNCIÓN: Resaltar términos de búsqueda**
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;
    
    const searchTerms = searchTerm.toLowerCase().split(' ');
    let highlightedText = text;
    
    searchTerms.forEach(term => {
      if (term) {
        const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
      }
    });
    
    return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  };

  // **NUEVA FUNCIÓN: Filtrado estricto por palabras completas**
  const filterProductsStrict = (products) => {
    let filtered = [...products];
    
    // BÚSQUEDA ESTRICTA - Por palabras completas en el nombre del producto
    if (searchTerm.trim()) {
      const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);
      filtered = filtered.filter(product => {
        const productName = product.nombre?.toLowerCase() || '';
        
      // Verificar que TODAS las palabras de búsqueda estén presentes como palabras completas
      return searchWords.every(searchWord => {
        // Crear expresión regular para buscar la palabra completa
        // \b indica límite de palabra, escapar caracteres especiales
        const escapedWord = searchWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const wordRegex = new RegExp(`\\b${escapedWord}\\b`, 'i');
        
        // Si la palabra contiene solo números, ser más flexible
        if (/^\d+$/.test(searchWord)) {
          // Para números, permitir que aparezcan solos o con guiones/letras
          const numberRegex = new RegExp(`\\b${escapedWord}(?:[a-z]*|\\-?[a-z]*)\\b|\\b[a-z]*\\-?${escapedWord}\\b`, 'i');
          return numberRegex.test(productName) || productName.includes(searchWord);
        }
        
        // Para palabras, buscar coincidencia exacta de palabra completa
        return wordRegex.test(productName);
      });
    });
  }
    
    // Filtrar por proveedor
    if (selectedProvider) {
      filtered = filtered.filter(product => 
        product.proveedor_id && product.proveedor_id.toString() === selectedProvider
      );
    }
    
    // Filtros adicionales
    if (productFilters.precio_min) {
      filtered = filtered.filter(product => 
        parseFloat(product.precio_sin_iva || 0) >= parseFloat(productFilters.precio_min)
      );
    }
    
    if (productFilters.precio_max) {
      filtered = filtered.filter(product => 
        parseFloat(product.precio_sin_iva || 0) <= parseFloat(productFilters.precio_max)
      );
    }
    
    if (productFilters.marca) {
      filtered = filtered.filter(product =>
        product.marca?.toLowerCase().includes(productFilters.marca.toLowerCase())
      );
    }
    
    if (productFilters.familia) {
      filtered = filtered.filter(product =>
        product.familia?.toLowerCase().includes(productFilters.familia.toLowerCase())
      );
    }
    
    // Ordenar por precio (menor a mayor) como en ProviderManagement
    filtered.sort((a, b) => {
      const priceA = parseFloat(a.precio_sin_iva) || 0;
      const priceB = parseFloat(b.precio_sin_iva) || 0;
      return priceA - priceB;
    });
    
    return filtered;
  };

  // **FUNCIÓN MODIFICADA: Aplicar filtros en tiempo real**
  useEffect(() => {
    const filtered = filterProductsStrict(products);
    setFilteredProducts(filtered);
    
    // Limpiar selección cuando cambien los productos filtrados
    setSelectedProducts(new Set());
    setSelectAll(false);
  }, [products, searchTerm, selectedProvider, productFilters]);

  // **NUEVA FUNCIÓN: Calcular precio más bajo de productos filtrados**
  const getLowestPrice = () => {
    if (filteredProducts.length === 0) return null;
    return Math.min(...filteredProducts.map(p => parseFloat(p.precio_sin_iva) || 0));
  };

  // **NUEVA FUNCIÓN: Verificar si es el precio más bajo**
  const isLowestPrice = (price) => {
    const lowest = getLowestPrice();
    return lowest !== null && Math.abs(parseFloat(price) - lowest) < 0.00001;
  };

  // **FUNCIÓN MODIFICADA: Cargar datos sin filtros desde el backend (solo datos base)**
  const loadData = async (clientId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Cargar TODOS los productos del cliente sin filtros (filtrado se hace en frontend)
      const productsUrl = `${API_URL}/api/clientes/${clientId}/productos`;
      
      // Cargar productos y proveedores en paralelo
      const [productsRes, providersRes] = await Promise.all([
        fetch(productsUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/clientes/${clientId}/proveedores`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (!productsRes.ok || !providersRes.ok) {
        throw new Error('Error al cargar datos');
      }
      
      const [productsData, providersData] = await Promise.all([
        productsRes.json(),
        providersRes.json()
      ]);
      
      setProducts(productsData.productos || []);
      setProviders(providersData.proveedores || []);
      
    } catch (error) {
      console.error('Error al cargar datos:', error);
      showMessage('Error al cargar los datos del cliente', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Cargar cliente activo al montar el componente
  useEffect(() => {
    const savedClient = sessionStorage.getItem('activeClient');
    if (savedClient) {
      try {
        const client = JSON.parse(savedClient);
        setActiveClient(client);
        loadData(client.id);
      } catch (err) {
        console.error('Error al cargar cliente:', err);
      }
    } 
  }, []);

  // **FUNCIÓN MODIFICADA: Limpiar filtros**
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedProvider('');
    setProductFilters({
      precio_min: '',
      precio_max: '',
      marca: '',
      familia: ''
    });
  };

  // Manejar cambios pendientes
  useEffect(() => {
    const changes = editedProducts.size;
    setPendingChanges(changes);
    
    // Prevenir salir con cambios sin guardar
    const handleBeforeUnload = (e) => {
      if (changes > 0) {
        e.preventDefault();
        e.returnValue = '¿Seguro que quieres salir? Hay cambios sin guardar.';
        return '¿Seguro que quieres salir? Hay cambios sin guardar.';
      }
    };
    
    if (changes > 0) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [editedProducts]);

  // Manejar cambios en productos
  const handleProductChange = (productId, field, value) => {
    const currentEdited = editedProducts.get(productId) || {};
    const updatedProduct = { ...currentEdited, [field]: value };
    
    // MAPEO CORRECTO DE CAMPOS ENTRE FRONTEND Y BACKEND
    const fieldMapping = {
      'unidad_basica': 'unidad_basica',        
      'unidadbasica': 'unidad_basica',         
      'precio_sin_iva': 'precio_sin_iva',
      'precio_ahorro_sin_iva': 'precio_ahorro_sin_iva'  // NUEVO: Manejo correcto
    };
    
    // Aplicar mapeo si es necesario
    const mappedField = fieldMapping[field] || field;
    updatedProduct[mappedField] = value;
    
    // CALCULAR PRECIO AHORRO CON IVA AUTOMÁTICAMENTE
    if (field === 'precio_ahorro_sin_iva' || field === 'tipo_iva') {
      // Obtener el precio ahorro sin IVA actual
      let precioAhorroSinIva;
      if (field === 'precio_ahorro_sin_iva') {
        precioAhorroSinIva = parseFloat(value) || 0;
      } else {
        // Si estamos cambiando tipo_iva, obtener el precio ahorro actual
        precioAhorroSinIva = parseFloat(updatedProduct.precio_ahorro_sin_iva) || 
                            parseFloat(currentEdited.precio_ahorro_sin_iva) || 
                            parseFloat(getOriginalProductValue(productId, 'precio_ahorro')) || 0;
      }
      
      // Obtener el tipo de IVA actual
      let tipoIva;
      if (field === 'tipo_iva') {
        tipoIva = parseInt(value) || 21;
      } else {
        tipoIva = parseInt(updatedProduct.tipo_iva) || 
                parseInt(currentEdited.tipo_iva) || 
                parseInt(getOriginalProductValue(productId, 'tipo_iva')) || 21;
      }
      
      // Calcular precio ahorro con IVA
      updatedProduct.precio_ahorro_con_iva = (precioAhorroSinIva * (1 + (tipoIva / 100))).toFixed(2);
    }
    
    // Obtener producto original para comparar cambios
    const originalProduct = products.find(p => p.id === productId);
    if (originalProduct) {
      // Solo actualizar si el valor realmente cambió
      if (value !== undefined && value !== null) {
        const newEditedProducts = new Map(editedProducts);
        newEditedProducts.set(productId, updatedProduct);
        setEditedProducts(newEditedProducts);
      }
    }
  };

  const getOriginalProductValue = (productId, field) => {
    const originalProduct = products.find(p => p.id === productId);
    if (!originalProduct) return '';
    
    // Mapeo de campos del backend
    const backendFieldMapping = {
      'precio_ahorro_sin_iva': 'precio_ahorro',  // Backend usa 'precio_ahorro' para sin IVA
      'precio_ahorro_con_iva': 'ivaahorro',      // Backend usa 'ivaahorro' para con IVA
      'unidad_basica': 'unidadbasica'            // Backend usa 'unidadbasica'
    };
    
    const backendField = backendFieldMapping[field] || field;
    return originalProduct[backendField];
  };

  // Selección de productos
  const handleSelectProduct = (productId, checked) => {
    const newSelected = new Set(selectedProducts);
    if (checked) {
      newSelected.add(productId);
    } else {
      newSelected.delete(productId);
    }
    setSelectedProducts(newSelected);
    setSelectAll(newSelected.size === filteredProducts.length);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = new Set(filteredProducts.map(p => p.id));
      setSelectedProducts(allIds);
    } else {
      setSelectedProducts(new Set());
    }
    setSelectAll(checked);
  };

  // Guardar cambios
  const handleSaveChanges = async () => {
    if (editedProducts.size === 0) {
      showMessage('No hay cambios para guardar', 'warning');
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const changesArray = Array.from(editedProducts.entries()).map(([id, changes]) => ({
        id: parseInt(id),
        ...changes
      }));
      
      const response = await fetch(`${API_URL}/api/productos/update-batch`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cliente_id: activeClient.id,
          productos: changesArray
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al guardar cambios');
      }
      
      const result = await response.json();
      showMessage(`${result.updated_count} productos actualizados correctamente`, 'success');
      
      // Limpiar cambios pendientes y recargar datos
      setEditedProducts(new Map());
      await loadData(activeClient.id);
      
    } catch (error) {
      console.error('Error al guardar:', error);
      showMessage(error.message || 'Error al guardar los cambios', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Aplicar familia a productos seleccionados
  const handleApplyFamily = async () => {
    if (selectedProducts.size === 0 || !selectedFamily) {
      showMessage('Selecciona productos y una familia', 'warning');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/productos/apply-family`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          producto_ids: Array.from(selectedProducts),
          familia: selectedFamily
        })
      });
      
      if (!response.ok) {
        throw new Error('Error al aplicar familia');
      }
      
      const result = await response.json();
      showMessage(`Familia aplicada a ${result.updated_count} productos`, 'success');
      
      // Limpiar selección y recargar datos
      setSelectedProducts(new Set());
      setSelectAll(false);
      await loadData(activeClient.id);
      
    } catch (error) {
      console.error('Error al aplicar familia:', error);
      showMessage(error.message || 'Error al aplicar familia', 'error');
    }
  };

  // Eliminar productos
  const handleDeleteProducts = async (productIds) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/productos/delete-batch`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cliente_id: activeClient.id,
          producto_ids: Array.isArray(productIds) ? productIds : [productIds]
        })
      });
      
      if (!response.ok) {
        throw new Error('Error al eliminar productos');
      }
      
      const result = await response.json();
      showMessage(`${result.deleted_count} productos eliminados correctamente`, 'success');
      
      // Limpiar selección y recargar datos
      setSelectedProducts(new Set());
      setSelectAll(false);
      setDeleteConfirm({ show: false, type: '', data: null });
      await loadData(activeClient.id);
      
    } catch (error) {
      console.error('Error al eliminar:', error);
      showMessage(error.message || 'Error al eliminar productos', 'error');
    }
  };

  // Eliminar múltiples productos
  const handleDeleteMultiple = () => {
    setDeleteConfirm({
      show: true,
      type: 'multiple',
      data: Array.from(selectedProducts)
    });
  };

  // Utilidades
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 8000);
  };

  const getProductValue = (product, field) => {
  const edited = editedProducts.get(product.id);
  
  // MAPEO CORRECTO de campos frontend/backend
  const fieldAliases = {
    'precio_ahorro_sin_iva': ['precio_ahorro_sin_iva', 'precio_ahorro'],  // Frontend -> Backend
    'precio_ahorro_con_iva': ['precio_ahorro_con_iva', 'ivaahorro'],      // Frontend -> Backend
    'unidad_basica': ['unidad_basica', 'unidadbasica'],                   // Frontend -> Backend
    'unidadbasica': ['unidad_basica', 'unidadbasica']
  };
  
  // Buscar en datos editados primero
  if (edited) {
    if (edited[field] !== undefined) {
      return edited[field];
    }
    
    // Buscar en aliases de edited
    const aliases = fieldAliases[field];
    if (aliases) {
      for (const alias of aliases) {
        if (edited[alias] !== undefined) {
          return edited[alias];
        }
      }
    }
  }
  
  // Buscar en el producto original
  if (product[field] !== undefined) {
    return product[field];
  }
  
  // Buscar en aliases del producto original
  const aliases = fieldAliases[field];
  if (aliases) {
    for (const alias of aliases) {
      if (product[alias] !== undefined) {
        return product[alias];
      }
    }
  }
  
  return '';
};

  const hasChanges = (productId) => {
    return editedProducts.has(productId);
  };

  // Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Componente de confirmación de eliminación
  const DeleteConfirmModal = () => {
    if (!deleteConfirm.show) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="ml-4 text-xl font-bold text-gray-900">Confirmar Eliminación</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              {deleteConfirm.type === 'individual' 
                ? `¿Estás seguro de eliminar "${deleteConfirm.data.nombre}"?`
                : `¿Estás seguro de eliminar ${deleteConfirm.data.length} productos seleccionados?`
              }
              <span className="block text-sm text-red-600 mt-2 font-semibold">Esta acción no se puede deshacer.</span>
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm({ show: false, type: '', data: null })}
                className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (deleteConfirm.type === 'individual') {
                    handleDeleteProducts(deleteConfirm.data.id);
                  } else {
                    handleDeleteProducts(deleteConfirm.data);
                  }
                }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Componente de carga
  if (loading && !products.length) {
    return (
      <div className="fixed inset-0 top-16 bg-gradient-to-br from-gray-50 to-gray-100 flex justify-center items-center">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="w-20 h-20 border-4 rounded-full animate-spin border-t-teal-600 mx-auto" style={{borderColor: '#14b8a640', borderTopColor: '#14b8a6'}}></div>
            <Package className="w-8 h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-teal-500" />
          </div>
          <h3 className="text-xl font-bold mb-2 text-teal-600">Cargando productos</h3>
          <p className="text-gray-600">Preparando el workspace de gestión...</p>
        </div>
      </div>
    );
  }

  if (!activeClient) {
    return (
      <div className="fixed inset-0 top-16 bg-gradient-to-br from-gray-50 to-gray-100 flex justify-center items-center">
        <div className="bg-red-100 text-red-800 p-6 rounded-xl shadow-lg max-w-lg w-full border border-red-200 mx-4">
          <div className="flex items-start">
            <AlertCircle size={28} className="text-red-500 mr-4 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-lg text-red-700 mb-2">Cliente Requerido</h3>
              <p className="text-red-700">Para gestionar productos, primero necesitas seleccionar un cliente activo desde el panel de gestión.</p>
              <button className="mt-4 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md transition-all duration-200 inline-flex items-center">
                <ArrowLeft size={16} className="mr-2" />
                Seleccionar Cliente
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 top-16 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
      
      {/* Panel Lateral */}
      <SidePanel
        isOpen={sidePanelOpen}
        setIsOpen={setSidePanelOpen}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedProvider={selectedProvider}
        setSelectedProvider={setSelectedProvider}
        providers={providers}
        handleClearFilters={handleClearFilters}
        selectedProducts={selectedProducts}
        selectedFamily={selectedFamily}
        setSelectedFamily={setSelectedFamily}
        handleApplyFamily={handleApplyFamily}
        handleDeleteMultiple={handleDeleteMultiple}
        pendingChanges={pendingChanges}
        handleSaveChanges={handleSaveChanges}
        saving={saving}
        productFilters={productFilters}
        setProductFilters={setProductFilters}
        familiasDisponibles={familiasDisponibles}
      />

      {/* Área Principal */}
      <div className="h-full flex flex-col" style={{ 
        marginLeft: sidePanelOpen ? '380px' : '0px',
        transition: 'margin-left 0.3s ease-in-out'
      }}>
        
        {/* Header */}
        <div className="flex-shrink-0 bg-white shadow-sm border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)' }}>
                <FileEdit size={20} style={{ color: '#14b8a6' }} />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-800">Gestión de Productos</h1>
                <p className="text-sm text-gray-600">Sistema de actualización avanzado</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">
                  {activeClient?.razon_social}
                </div>
                <div className="text-xs text-gray-500">
                  {filteredProducts.length} de {products.length} productos mostrados
                </div>
              </div>
            </div>
          </div>
          
          {/* Información de búsqueda */}
          {searchTerm && (
            <div className="mt-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center text-sm text-green-800">
                <Search className="w-4 h-4 mr-2" />
                <span className="font-medium">
                  Búsqueda activa: "{searchTerm}" - {filteredProducts.length} productos encontrados
                </span>
              </div>
              <p className="text-xs text-green-600 mt-1">
                Productos ordenados por precio (menor a mayor)
              </p>
            </div>
          )}
        </div>

        {/* Contenido Principal */}
        <div className="flex-1 overflow-auto">
          <div className="h-full" style={{ 
            paddingLeft: '53px', 
            paddingRight: sidePanelOpen ? '25px' : '16px',
            paddingTop: '24px', 
            paddingBottom: '24px',
            transition: 'padding-right 0.3s ease-in-out'
          }}>

            {/* Mensajes de alerta */}
            {message.text && (
              <div className={`alert mb-6 p-4 rounded-xl shadow-md transition-all duration-200 transform ${
                message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : 
                message.type === "error" ? "bg-red-50 text-red-800 border border-red-200" :
                "bg-yellow-50 text-yellow-800 border border-yellow-200"
              }`}>
                <div className="flex items-start">
                  {message.type === "success" ? (
                    <Check size={24} className="text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle size={24} className="text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{message.text}</p>
                  </div>
                  <button 
                    className="text-gray-500 hover:text-gray-700 transition-colors ml-2" 
                    onClick={() => setMessage({ text: "", type: "" })}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            )}

            {/* Tabla de productos */}
            {filteredProducts.length > 0 ? (
              <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 h-full flex flex-col">
                {/* Header de la tabla */}
                <div className="p-3 bg-teal-500 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <ShoppingCart className="w-5 h-5" />
                      <h3 className="text-lg font-bold">Catálogo de Productos</h3>
                      {searchTerm && (
                        <div className="flex items-center space-x-1 px-2 py-1 rounded text-xs border border-white border-opacity-40 bg-teal-600">
                          <Search className="w-3 h-3" />
                          <span className="font-medium">Búsqueda estricta: "{searchTerm}"</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="w-4 h-4 rounded focus:ring-2 mr-2 accent-teal-500"
                        />
                        <span className="font-medium text-sm">Seleccionar todos</span>
                      </label>
                      {selectedProducts.size > 0 && (
                        <div className="px-2 py-1 rounded text-sm border border-white border-opacity-40 bg-teal-600">
                          <span className="font-bold">{selectedProducts.size} seleccionados</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tabla scrollable */}
                <div className="flex-1 overflow-auto">
                  <table className="w-full table-auto divide-y divide-gray-200">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 w-10">Sel.</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 w-14">ID</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 min-w-[300px]">Nombre del Producto</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 min-w-[140px]">Marca</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 min-w-[140px]">Ref. Proveedor</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 min-w-[120px]">Unidad</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 min-w-[120px]">Embalaje</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 min-w-[130px]">
                          <div className="flex items-center space-x-1">
                            <span>Precio s/IVA €</span>
                            <ArrowUp className="w-3 h-3" />
                          </div>
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 min-w-[130px]">Ahorro s/IVA €</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 min-w-[130px]">Ahorro c/IVA €</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 w-20">IVA %</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 min-w-[160px]">Proveedor</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 min-w-[200px]">Familia</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 min-w-[140px]" title="Solo aplica a cls 131/999999">Subfamilia</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-teal-600 w-24">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {filteredProducts.map((product, index) => (
                        <tr 
                          key={product.id} 
                          className={`transition-colors hover:bg-gray-50 ${
                            hasChanges(product.id) ? 'border-l-4 border-amber-400 bg-amber-50' : 
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }`}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedProducts.has(product.id)}
                              onChange={(e) => handleSelectProduct(product.id, e.target.checked)}
                              className="w-4 h-4 rounded focus:ring-2 accent-teal-500"
                            />
                          </td>
                          <td className="px-3 py-2 text-sm font-bold text-gray-800">{product.id}</td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={getProductValue(product, 'nombre')}
                              onChange={(e) => handleProductChange(product.id, 'nombre', e.target.value)}
                              className="w-full px-2 py-1 text-sm border-2 rounded focus:outline-none focus:border-teal-500 transition-all bg-white border-gray-200"
                            />
                            {/* Mostrar texto resaltado debajo del input para mostrar coincidencias */}
                            {searchTerm && (
                              <div className="text-xs mt-1 text-gray-600">
                                {highlightSearchTerm(product.nombre, searchTerm)}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={getProductValue(product, 'marca') || ''}
                              onChange={(e) => handleProductChange(product.id, 'marca', e.target.value)}
                              className="w-full px-2 py-1 text-sm border-2 rounded focus:outline-none focus:border-teal-500 transition-all bg-white border-gray-200"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={getProductValue(product, 'ref_proveedor') || ''}
                              onChange={(e) => handleProductChange(product.id, 'ref_proveedor', e.target.value)}
                              className="w-full px-2 py-1 text-sm border-2 rounded focus:outline-none focus:border-teal-500 transition-all bg-teal-50 border-teal-200"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={getProductValue(product, 'unidadbasica') || ''}  // Usar 'unidadbasica' como en BD
                              onChange={(e) => handleProductChange(product.id, 'unidad_basica', e.target.value)}  // Enviar como 'unidad_basica'
                              className="w-full px-2 py-1 text-sm border-2 rounded focus:outline-none focus:border-teal-500 transition-all bg-teal-50 border-teal-200"
                            />
                          </td>

                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={getProductValue(product, 'cantidad_embalaje') || ''}
                              onChange={(e) => handleProductChange(product.id, 'cantidad_embalaje', e.target.value)}
                              className="w-full px-2 py-1 text-sm border-2 rounded focus:outline-none focus:border-teal-500 transition-all bg-white border-gray-200"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="0.0001"
                              value={parseFloat(getProductValue(product, 'precio_sin_iva') || 0).toFixed(4)}
                              onChange={(e) => handleProductChange(product.id, 'precio_sin_iva', e.target.value)}
                              className={`w-full px-2 py-1 text-sm border-2 rounded focus:outline-none transition-all font-bold ${
                                isLowestPrice(getProductValue(product, 'precio_sin_iva')) 
                                  ? 'bg-green-50 border-green-400 text-green-800 focus:border-green-500' 
                                  : 'bg-white border-gray-200 text-gray-800 focus:border-teal-500'
                              }`}
                              title={isLowestPrice(getProductValue(product, 'precio_sin_iva')) ? 'Este es el precio más bajo de los productos mostrados' : ''}
                            />
                            {isLowestPrice(getProductValue(product, 'precio_sin_iva')) && (
                              <div className="flex items-center justify-center mt-1">
                                <div className="flex items-center space-x-1 px-2 py-1 rounded text-xs font-bold bg-green-600 text-white">
                                  <TrendingUp className="w-3 h-3" />
                                  <span>MEJOR</span>
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="0.01"
                              value={getProductValue(product, 'precio_ahorro_sin_iva') || ''}
                              onChange={(e) => handleProductChange(product.id, 'precio_ahorro_sin_iva', e.target.value)}
                              className="w-full px-2 py-1 text-sm border-2 rounded focus:outline-none focus:border-orange-500 transition-all bg-orange-50 border-orange-200 font-medium"
                              placeholder="0.00"
                            />
                            <p className="text-xs text-orange-600 mt-1">Precio anterior cliente</p>
                          </td>

                          {/* Precio Ahorro Con IVA - CORREGIDO */}
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="0.01"
                              value={getProductValue(product, 'precio_ahorro_con_iva') || ''}
                              onChange={(e) => handleProductChange(product.id, 'precio_ahorro_con_iva', e.target.value)}
                              className="w-full px-2 py-1 text-sm border-2 rounded focus:outline-none focus:border-orange-500 transition-all bg-orange-100 border-orange-300 font-medium"
                              placeholder="0.00"
                            />
                            <p className="text-xs text-orange-600 mt-1">Calculado automáticamente</p>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={getProductValue(product, 'tipo_iva') || '21'}
                              onChange={(e) => handleProductChange(product.id, 'tipo_iva', e.target.value)}
                              className="w-full px-2 py-1 text-sm border-2 rounded focus:outline-none focus:border-teal-500 transition-all bg-white border-gray-200"
                            >
                              <option value="21">21%</option>
                              <option value="10">10%</option>
                              <option value="4">4%</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={getProductValue(product, 'proveedor_id') || ''}
                              onChange={(e) => handleProductChange(product.id, 'proveedor_id', e.target.value)}
                              className="w-full px-2 py-1 text-sm border-2 rounded focus:outline-none focus:border-teal-500 transition-all bg-white border-gray-200"
                            >
                              <option value="">Sin proveedor</option>
                              {providers.map(provider => (
                                <option key={provider.id} value={provider.id}>
                                  {provider.nombre}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={getProductValue(product, 'familia') || 'Sin familia'}
                              onChange={(e) => handleProductChange(product.id, 'familia', e.target.value)}
                              className="w-full px-2 py-1 text-sm border-2 rounded focus:outline-none focus:border-teal-500 transition-all bg-white border-gray-200"
                            >
                              <option value="Sin familia">Sin familia</option>
                              {familiasDisponibles.map(familia => (
                                <option key={familia} value={familia}>
                                  {familia}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={getProductValue(product, 'subfamilia') || ''}
                              onChange={(e) => handleProductChange(product.id, 'subfamilia', e.target.value)}
                              className="w-full px-2 py-1 text-sm border-2 rounded focus:outline-none focus:border-teal-500 transition-all bg-white border-gray-200"
                              title="Cirugía / Medicina. Solo afecta a las clínicas 131 y 999999."
                            >
                              <option value="">— Sanitario —</option>
                              <option value="cirugia">Cirugía</option>
                              <option value="medicina">Medicina</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setBarcodesModal({ open: true, product })}
                                className="text-teal-600 hover:text-teal-800 p-1 rounded transition-colors border bg-teal-50 border-teal-200 hover:bg-teal-100"
                                title="Códigos de barras"
                              >
                                <Barcode className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setDeleteConfirm({
                                  show: true,
                                  type: 'individual',
                                  data: product
                                })}
                                className="text-red-600 hover:text-red-800 p-1 rounded transition-colors border bg-red-50 border-red-200 hover:bg-red-100"
                                title="Eliminar producto"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Footer con resumen */}
                <div className="p-4 bg-gray-50 border-t">
                  <div className="flex justify-between items-center text-sm">
                    <div className="flex items-center space-x-4">
                      <span className="text-gray-600">
                        Mostrando <strong>{filteredProducts.length}</strong> de <strong>{products.length}</strong> productos
                      </span>
                      {searchTerm && (
                        <span className="text-green-600 font-medium">
                          Filtrado por: "{searchTerm}"
                        </span>
                      )}
                    </div>
                    {filteredProducts.length > 0 && (
                      <div className="flex items-center space-x-2 text-green-700">
                        <TrendingUp className="w-4 h-4" />
                        <span className="font-medium">
                          Precio más bajo: {formatCurrency(getLowestPrice())}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white p-10 rounded-xl text-center border border-yellow-200 shadow-md">
                <div className="flex flex-col items-center justify-center">
                  <div className="bg-yellow-100 p-4 rounded-full mb-4">
                    <AlertCircle size={40} className="text-yellow-500" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-800 mb-2">No hay productos</h3>
                  <p className="text-gray-600 max-w-lg mx-auto mb-6">
                    {searchTerm || selectedProvider || productFilters.precio_min || productFilters.precio_max || productFilters.marca || productFilters.familia
                      ? 'No se encontraron productos con los filtros aplicados. Ajusta los criterios de búsqueda.'
                      : 'No se encontraron productos para este cliente. Verifica los datos del cliente o importa productos.'
                    }
                  </p>
                  {searchTerm && (
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 max-w-md">
                      <p className="text-sm text-blue-800 font-medium">
                        Búsqueda estricta activa: "{searchTerm}"
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        Se requiere que todas las palabras coincidan en el nombre del producto
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de gestión de códigos de barras */}
      <BarcodesProductModal
        open={barcodesModal.open}
        product={barcodesModal.product}
        onClose={() => setBarcodesModal({ open: false, product: null })}
      />

      {/* Modal de confirmación de eliminación */}
      <DeleteConfirmModal />
    </div>
  );
}
